/* eslint-disable @typescript-eslint/no-explicit-any */
import { Image, Canvas, Path, FabricObject } from 'fabric';

import { MenuItemWithBackup } from '../types/types';

import { generateObjectId } from './generateId';
import { loadObjectsWithLayers } from './loadObjectsWithLayers';
import { makeColorizedUrlForObject } from './makeColorizedUrlForObject';

interface ImageInfo {
  preview: string;
  image: string;
  source_ext: string;
}

export const scaleImage = async (
  imageObject: Image,
  targetWidth: number,
  targetHeight: number,
  maskVisualWidth?: number,
  maskVisualHeight?: number,
) => {
  const originalWidth = imageObject._originalElement.width;
  const originalHeight = imageObject._originalElement.height;

  const clipPath = imageObject.clipPath as Path;

  if (clipPath && maskVisualWidth && maskVisualHeight) {
    // Cover-стратегия: новая картинка должна полностью покрывать область маски
    // (аналог бэкенда: Math.max(refWidth / imgWidth, refHeight / imgHeight))
    const scale = Math.max(maskVisualWidth / originalWidth, maskVisualHeight / originalHeight);

    imageObject.width = originalWidth;
    imageObject.height = originalHeight;
    imageObject.scale(scale);

    clipPath.scaleX = 1 / scale;
    clipPath.scaleY = 1 / scale;
    // Центрируем маску на новом изображении (0,0 = центр в локальных координатах)
    clipPath.left = 0;
    clipPath.top = 0;

    imageObject.perPixelTargetFind = true;
  } else {
    // Обычная картинка без маски
    const scaleX = targetWidth / originalWidth;
    const scaleY = targetHeight / originalHeight;
    const imageMaxScale = Math.max(scaleX, scaleY);

    imageObject.width = originalWidth;
    imageObject.height = originalHeight;

    if (clipPath) {
      const scale = Math.max(targetWidth / originalWidth, targetHeight / originalHeight);
      clipPath.scale(1 / scale);
      imageObject.perPixelTargetFind = true;
    }

    imageObject.scale(imageMaxScale);
  }

  imageObject.isNeedScaleImgForFirstRender = false;
  imageObject.objectCaching = false;
  imageObject.dirty = true;
};

export const changeImage = async ({
  canvas,
  canvasId,
  newImageSrc: src,
  sourceExt,
  addBackup,
  sourceType,
  themeId,
  targetId,
}: Omit<MenuItemWithBackup, 'closeOnClick' | 'item' | 'targetIds'> & {
  newImageSrc: string;
  sourceExt: string;
  sourceType: string | undefined;
  themeId?: string;
  targetId?: string;
}) => {
  let activeObject: FabricObject | undefined = undefined;

  if (typeof targetId === 'string' && targetId.length > 0) {
    const allObjects = canvas.getObjects();
    const found = allObjects.find(
      (obj) => obj.id === targetId && obj.type && obj.type.toLowerCase() === 'image',
    );
    if (found) {
      activeObject = found;
    }
  } else {
    activeObject = canvas.getActiveObject();
  }

  if (activeObject) {
    if (activeObject.type.toLowerCase() === 'image') {
      const targetWidth = activeObject.width * activeObject.scaleX;
      const targetHeight = activeObject.height * activeObject.scaleY;

      // Вычисляем визуальные размеры маски до удаления объекта
      const oldClipPath = activeObject.clipPath as Path | undefined;
      let maskVisualWidth: number | undefined;
      let maskVisualHeight: number | undefined;

      if (oldClipPath) {
        maskVisualWidth = oldClipPath.getScaledWidth() * activeObject.scaleX;
        maskVisualHeight = oldClipPath.getScaledHeight() * activeObject.scaleY;
      }

      //Remove img
      canvas.remove(activeObject);

      makeColorizedUrlForObject(activeObject, activeObject.imageColor);

      const img = await Image.fromURL(src, {
        crossOrigin: 'anonymous',
      });

      // Attach previous clipPath BEFORE scaling so inverse scaling is applied correctly
      if (oldClipPath) {
        img.clipPath = oldClipPath;
        // ✅ Включаем попиксельное определение клика для изображений с маской
        img.perPixelTargetFind = true;
      }

      // Scale new image to cover the previous object's visual bounds (preserves mask fit)
      scaleImage(img, targetWidth, targetHeight, maskVisualWidth, maskVisualHeight);

      //TODO: add work with mask img
      img.set({
        angle: activeObject.angle,
        left: activeObject.left,
        top: activeObject.top,
        category: activeObject.category,
        media_type: activeObject.media_type,
        id: generateObjectId(canvasId),
        originX: activeObject.originX,
        originY: activeObject.originY,
        noCopy: activeObject.noCopy,
        available_images: activeObject.available_images,
        source_ext: sourceExt,
        layer: activeObject.layer,
        zIndex: activeObject.zIndex,
        name: activeObject.name,
        clip_path: activeObject.clip_path,
        clipPath: activeObject.clipPath,
        source_width: activeObject.source_width,
        source_height: activeObject.source_height,
        inner_id: activeObject.inner_id,
        crop: activeObject.crop,
        source_type: sourceType || activeObject.source_type,
        theme_id: themeId,
      });

      canvas.add(img);
    }
    addBackup(canvasId, canvas.toJSON());
    loadObjectsWithLayers(canvas, canvasId, canvas.toJSON()).then(() => {
      canvas.requestRenderAll();
    });
  }
};

/**
 * Add new image to canvas
 * @param {Object} params - Parameters for adding an image
 * @param {Canvas} params.canvas - Canvas instance
 * @param {string} params.canvasId - Canvas ID
 * @param {string} params.imageSrc - URL of the image to add
 * @param {string} params.sourceExt - Extension of the original image file
 * @param {string} params.mediaType - Media type ('object' or 'photo' or another)
 * @param {function} params.addBackup - Function to create a backup
 * @param {Array} params.availableImages - Array of available images
 * @param {string} params.layer - Layer of the image
 * @param {string} params.themeId - Theme ID
 * @returns {Promise<Image>} Promise that resolves with the added image or rejects with an error
 */
export const addNewImage = ({
  canvas,
  canvasId,
  imageSrc,
  sourceExt = '',
  addBackup,
  availableImages = [],
  mediaType,
  layer = 'middle',
  sourceType,
  zIndex = 1,
  themeId,
}: {
  canvas: Canvas;
  canvasId: string;
  imageSrc: string;
  sourceExt?: string;
  mediaType?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addBackup: (canvasId: string, data: any) => void;
  availableImages?: ImageInfo[];
  layer?: string;
  sourceType?: string;
  zIndex?: number;
  themeId?: string | number | null;
}): Promise<Image> => {
  return new Promise((resolve, reject) => {
    try {
      const canvasWidth = canvas.getWidth();
      const canvasHeight = canvas.getHeight();

      Image.fromURL(imageSrc, { crossOrigin: 'anonymous' })
        .then((fabricImage) => {
          try {
            const width = fabricImage.width || 100;
            const height = fabricImage.height || 100;

            const scaleFactor = Math.min(
              (canvasWidth * 0.5) / width,
              (canvasHeight * 0.5) / height,
            );

            const finalWidth = width * scaleFactor;

            fabricImage.set({
              id: generateObjectId(canvasId),
              left: canvasWidth / 2 - finalWidth / 2,
              top: canvasHeight / 2,
              media_type: mediaType,
              source_ext: sourceExt,
              available_images: availableImages,
              crop: {
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
              },
              inner_id: '', //need for backend
              layer,
              scaleX: scaleFactor,
              scaleY: scaleFactor,
              name: `image_${Date.now()}`,
              zIndex,
              isNeedScaleImgForFirstRender: false,
              originX: 'center',
              originY: 'center',
              source_type: sourceType,
              theme_id: themeId,
            });

            // Add image to canvas
            canvas.add(fabricImage);

            // Make image active and render before creating backup
            canvas.setActiveObject(fabricImage);
            canvas.requestRenderAll();

            // Create backup
            const cfgToBackup = canvas.toJSON();
            cfgToBackup.isChangedContent = true; // Need for backend for layouts

            addBackup(canvasId, cfgToBackup);

            // Save image ID for further use
            const imageId = fabricImage.id;

            loadObjectsWithLayers(canvas, canvasId, cfgToBackup).then(() => {
              // Find object with the same ID and make it active
              const objects = canvas.getObjects();
              const newImageObject = objects.find((obj) => obj.id === imageId);

              if (newImageObject) {
                requestAnimationFrame(() => {
                  canvas.setActiveObject(newImageObject);
                  canvas.requestRenderAll();
                });
              }
            });

            resolve(fabricImage);
          } catch (innerError) {
            reject(innerError);
          }
        })
        .catch((error) => {
          console.error('Error loading image:', error);
          reject(error);
        });
    } catch (error) {
      console.error('Error adding new image to canvas:', error);
      reject(error);
    }
  });
};

export const isUserImage = (image: FabricObject) => {
  const sourceType = (image as any)?.source_type;
  const name = (image as any)?.name;
  return (
    typeof sourceType === 'string' &&
    sourceType.toLowerCase() === 'user' &&
    typeof name === 'string' &&
    name.toLowerCase() !== 'visual_placeholder'
  );
};

export const isHaveUserImage = (objects: FabricObject[]) => {
  return objects.some((obj) => isUserImage(obj));
};
